// File: frontend/src/pages/KnowledgeGraph/components/GraphControls.tsx
import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  Switch,
  Stack,
  Paper,
  Typography,
  Tooltip,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import { ENTITY_TYPE_COLORS, ENTITY_TYPE_LABELS } from '../types';
import { cyber } from '../../../theme';

export type GraphLayoutMode = 'force' | 'radial';

export interface LegendItem {
  type: string;
  label: string;
  count?: number;
}

const DEFAULT_LEGEND: LegendItem[] = [
  { type: 'ThreatActor', label: ENTITY_TYPE_LABELS.ThreatActor },
  { type: 'Malware', label: ENTITY_TYPE_LABELS.Malware },
  { type: 'Tool', label: ENTITY_TYPE_LABELS.Tool },
  { type: 'AttackPattern', label: ENTITY_TYPE_LABELS.AttackPattern },
  { type: 'Campaign', label: ENTITY_TYPE_LABELS.Campaign },
];

interface GraphControlsProps {
  depth: number;
  onDepthChange: (d: number) => void;
  showInferred: boolean;
  onShowInferredChange: (v: boolean) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  onExportPng?: () => void;
  layoutMode?: GraphLayoutMode;
  onLayoutModeChange?: (mode: GraphLayoutMode) => void;
  legendItems?: LegendItem[];
  hiddenTypes?: string[];
  onToggleType?: (type: string) => void;
}

const GraphControls: React.FC<GraphControlsProps> = ({
  depth,
  onDepthChange,
  showInferred,
  onShowInferredChange,
  onZoomIn,
  onZoomOut,
  onFit,
  onExportPng,
  layoutMode,
  onLayoutModeChange,
  legendItems,
  hiddenTypes = [],
  onToggleType,
}) => {
  const items = legendItems && legendItems.length > 0 ? legendItems : DEFAULT_LEGEND;

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 10,
        p: 1.5,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
        backdropFilter: 'blur(8px)',
        borderRadius: 2,
        minWidth: 170,
        border: `1px solid ${alpha(cyber.primary, 0.2)}`,
        boxShadow: 'none',
      }}
    >
      <Stack spacing={1.5}>
        {/* Depth */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Depth
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            {[1, 2, 3].map((d) => (
              <Button
                key={d}
                onClick={() => onDepthChange(d)}
                variant={depth === d ? 'contained' : 'outlined'}
                sx={{ minWidth: 32 }}
              >
                {d}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {/* Layout mode (2D only) */}
        {layoutMode && onLayoutModeChange && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Layout
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button
                onClick={() => onLayoutModeChange('force')}
                variant={layoutMode === 'force' ? 'contained' : 'outlined'}
              >
                Force
              </Button>
              <Button
                onClick={() => onLayoutModeChange('radial')}
                variant={layoutMode === 'radial' ? 'contained' : 'outlined'}
              >
                Radial
              </Button>
            </ButtonGroup>
          </Box>
        )}

        {/* Inferred toggle */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInferred}
              onChange={(e) => onShowInferredChange(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Inferred relations</Typography>}
        />

        {/* Zoom + export */}
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Zoom in">
            <Button size="small" variant="outlined" onClick={onZoomIn} sx={{ minWidth: 32 }}>
              <AddIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Zoom out">
            <Button size="small" variant="outlined" onClick={onZoomOut} sx={{ minWidth: 32 }}>
              <RemoveIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Fit to view">
            <Button size="small" variant="outlined" onClick={onFit} sx={{ minWidth: 32 }}>
              <FitScreenIcon fontSize="small" />
            </Button>
          </Tooltip>
          {onExportPng && (
            <Tooltip title="Export as PNG">
              <Button size="small" variant="outlined" onClick={onExportPng} sx={{ minWidth: 32 }}>
                <FileDownloadOutlinedIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}
        </Stack>

        {/* Interactive legend: click a type to hide/show it */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Legend {onToggleType ? '· click to filter' : ''}
          </Typography>
          {items.map((item) => {
            const hidden = hiddenTypes.includes(item.type);
            return (
              <Stack
                key={item.type}
                direction="row"
                spacing={0.75}
                alignItems="center"
                onClick={onToggleType ? () => onToggleType(item.type) : undefined}
                sx={{
                  mb: 0.25,
                  ...(onToggleType && {
                    cursor: 'pointer',
                    userSelect: 'none',
                    opacity: hidden ? 0.35 : 1,
                    '&:hover': { opacity: hidden ? 0.55 : 0.8 },
                  }),
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    flexShrink: 0,
                    borderRadius: item.type === 'ThreatActor' ? 0 : '50%',
                    transform: item.type === 'ThreatActor' ? 'rotate(45deg)' : undefined,
                    bgcolor: ENTITY_TYPE_COLORS[item.type] || '#6b7280',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', textDecoration: hidden ? 'line-through' : 'none' }}>
                  {item.label}
                  {item.count !== undefined && (
                    <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                      · {item.count}
                    </Box>
                  )}
                </Typography>
              </Stack>
            );
          })}
        </Box>

        {/* Interaction hint */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TouchAppOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
            Double-click a node to expand it
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default GraphControls;
